import type {
  Catchup,
  CurrentTvShow,
  LiveEvent,
  SeriesPlaylist,
  TvChannel,
  Vod,
} from '@setplex/tria-api'
import type { SendEventParams } from '~/shared/analytix'
import type { PlayerMeta } from '../player-meta'

import { Events } from '@setplex/pureya'
import { api } from '@setplex/tria-api'
import { combine, createWatch, merge, restore, sample } from 'effector'
import { debounce } from 'patronum'
import { model as catchupsModel } from '~/entities/catchups'
import { model as movieEntityModel } from '~/entities/movies'
import { playerEventModel, playerTvModel } from '~/entities/player'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { mq, model as mqModel, PLAYER } from '~/shared/mq'
import { SnapshotMap } from '../../general/snapshot-map'
// Legacy route-derived source attribution (`contentOpenFrom`) reused here.
import { $contentOpenFrom } from '../../model/player/routes'
import { stripHashFromUrl } from '../lib'
import { PlayerMetaMessage, PlayerMountedMessage } from '../player-meta'

const SNAPSHOTS_TO_KEEP = 10

export type PlayerOpenParameters = SendEventParams.PlayerOpen & {
  '(playerId)'?: string[] // auxiliary parameter to store players id for internal use, it will not be sent to analytix
}

/**
 * SnapshotMap to store `player_open` event parameters with their history
 */
export const parameters = new SnapshotMap<PlayerOpenParameters>(
  SNAPSHOTS_TO_KEEP
)

//
// Update parameters
//

function fromPlayerInstance(payload: { playerId: string }) {
  const current = new Set(parameters.get('(playerId)') || [])
  current.add(payload.playerId)
  parameters //
    .snapshot()
    .set('(playerId)', [...current])
    .notify()
}

function fromPlayerDisposal(payload: { playerId: string }) {
  const current = new Set(parameters.get('(playerId)') || [])
  current.delete(payload.playerId)
  parameters //
    .snapshot()
    .set('(playerId)', [...current])
    .notify()
}

function fromMetaAndContent(
  params: PlayerMeta,
  content: {
    channel?: TvChannel | null
    epg?: TvChannel | null
    vod?: Vod | null
    tvshow?: CurrentTvShow | null
    tvshowPlaylist?: SeriesPlaylist | null
    event?: LiveEvent | null
    catchup?: Catchup | null
  }
) {
  const type = params.type
  const { channel, epg, vod, tvshow, tvshowPlaylist, event, catchup } = content
  // prettier-ignore
  parameters
    .update('contentId', params.id)
    .update('contentType', params.trailer ? 'trailer' : type)
    .update('contentUrl', stripHashFromUrl(params.url))
    .update('contentName', params.name)
    .update('programName', params.program)
    .update('isContentPaid', params.paid)
    .tap(() => type === 'tv' && epg && tapFromEpg(epg))
    .tap(() => type === 'tv' && channel && tapFromChannel(channel))
    .tap(() => type === 'vod' && vod && tapFromVod(vod))
    .tap(() => type === 'tvshow' && tvshowPlaylist && tapFromTvShowPlaylist(tvshowPlaylist))
    .tap(() => type === 'tvshow' && tvshow && tapFromTvShow(tvshow))
    .tap(() => type === 'event' && event && tapFromEvent(event))
    .tap(() => type === 'catchup' && catchup && tapFromCatchup(catchup))
    .tap(() => params.trailer && tapFromTrailer())
    .commit()
    .notify()
}

function tapFromChannel(channel: TvChannel) {
  parameters //
    .update('contentName', channel.name)
}

function tapFromEpg(epg: TvChannel) {
  parameters //
    .update('contentName', epg.name)
}

function tapFromVod(vod: Vod) {
  parameters //
    .update('contentName', vod.name)
    .update('programName', '')
}

function tapFromTvShowPlaylist(tvshow: SeriesPlaylist) {
  const programName = `S${tvshow?.currentSeason?.displayNumber || 1}:E${tvshow?.currentEpisode?.displayNumber || 1}${tvshow?.currentEpisode?.name ? `-${tvshow?.currentEpisode.name}` : tvshow?.currentTvShow?.name ? `-${tvshow?.currentTvShow.name}` : ''}`
  parameters //
    .update('contentName', tvshow.currentTvShow?.name)
    .update('programName', programName)
}

function tapFromTvShow(tvshow: CurrentTvShow) {
  parameters //
    .update('contentName', tvshow.name)
  // .update('programName', programName)
}

function tapFromEvent(event: LiveEvent) {
  parameters //
    .update('contentName', event.name)
    .update('programName', event.startTime)
}

function tapFromCatchup(catchup: Catchup) {
  parameters //
    .update('contentName', catchup.channel?.name)
}

function tapFromTrailer() {
  parameters //
    // .update('contentName', undefined) // <- name of the movie/show
    .update('programName', '')
}

function fromUrl(url: string) {
  parameters //
    .update('contentUrl', stripHashFromUrl(url))
    .commit()
    .notify()
}

function fromNewUrl() {
  parameters //
    .snapshot()
    .remove('contentUrl')
    .notify()
}

function fromContentOpenFrom(contentOpenFrom: string) {
  parameters //
    .update('contentOpenFrom', contentOpenFrom)
    .commit()
    .notify()
}

//
// Updates listeners
//

// on new player instantiation
mq.on(`${PLAYER}/${Events.Player.Instantiation}`, (payload) => {
  fromPlayerInstance(payload)
})

// on player mounted to react tree
mq.on(PlayerMountedMessage, (payload) => {
  if (payload.mounted) {
    fromPlayerInstance({ playerId: payload.player.id_ })
  }
})

// on player disposal
mq.on(`${PLAYER}/dispose`, (payload) => {
  fromPlayerDisposal(payload)
})

// combine all possible content sources to update parameters on any content change
const $rawContent = combine({
  channel: playerTvModel.$currentChannelSSE,
  epg: selectEpgChannelModel.$active,
  vod: movieEntityModel.$vodFormatted,
  tvshow: api.tvshows.$currentTvShow,
  tvshowPlaylist: api.tvshows.$seriesPlaylist,
  event: playerEventModel.$currentEvent,
  catchup: catchupsModel.$activeCatchup,
})

// debounce content updates to avoid multiple rapid updates on player open
const $content = combine(
  $rawContent,
  restore(debounce($rawContent, 100), null),
  (raw, debounced) => debounced || raw
)

// on player params or content update
createWatch({
  unit: sample({
    source: {
      message: restore(mqModel.on(PlayerMetaMessage), null),
      content: $content,
    },
    filter: ({ message, content }) => message != null && content != null,
    fn: ({ message, content }) => ({
      meta: message!.meta,
      content: content!,
    }),
  }),
  fn({ meta, content }) {
    fromMetaAndContent(meta, content)
  },
})

// on content open source change
createWatch({
  unit: $contentOpenFrom,
  fn: (contentOpenFrom) => {
    fromContentOpenFrom(contentOpenFrom)
  },
})

// on fetch content url initiation
createWatch({
  unit: merge([
    api.catchups.getCatchupUrlFx,
    api.liveEvents.getLiveEventUrlByIdFx,
    api.tv.getTvChannelUrlFx,
    api.tv.getTvChannelUrlAbortableFx,
    api.tvshows.getTvShowTrailerUrlFx,
    api.tvshows.getTvShowEpisodeUrlFx,
    api.tvshows.getEpisodeUrlWithoutSeasonFx,
    api.vods.getUrlFx,
    api.vods.getTrailerUrlFx,
  ]),
  fn() {
    fromNewUrl()
  },
})

// on fetch content url success or failure
createWatch({
  unit: merge([
    api.catchups.getCatchupUrlFx.finally,
    api.liveEvents.getLiveEventUrlByIdFx.finally,
    api.tv.getTvChannelUrlFx.finally,
    api.tv.getTvChannelUrlAbortableFx.finally,
    api.tvshows.getTvShowTrailerUrlFx.finally,
    api.tvshows.getTvShowEpisodeUrlFx.finally,
    api.tvshows.getEpisodeUrlWithoutSeasonFx.finally,
    api.vods.getUrlFx.finally,
    api.vods.getTrailerUrlFx.finally,
  ]),
  fn(result) {
    fromUrl(result.status === 'done' ? result.result.playbackUrl : '')
  },
})
